import { fitWithin } from '@shared/image/fit'

/**
 * A foto do cupom, encolhida antes de subir.
 *
 * ## Três motivos que se somam, e o terceiro é o que manda no número
 *
 * 1. **A imagem é guardada para sempre.** `Receipt.md`: *"uma imagem de nota
 *    nunca é apagada depois de `confirmed`. É a prova do que foi extraído."*
 *    Cada megabyte é permanente.
 * 2. **Ela vira base64 dentro da chamada de IA**, e resolução vira token, que
 *    vira dinheiro por nota.
 * 3. **Ela precisa continuar LEGÍVEL** — pelo modelo hoje e por uma pessoa daqui
 *    a dois anos. É a restrição que decide todo o resto: não adianta chegar a
 *    50 KB se o texto do cupom virou borrão.
 *
 * ## `LADO_MAXIMO` de 3200 — era 1600, e o teste de realidade chegou
 *
 * A conta original supunha um cupom **ocupando a foto inteira**: 50 linhas a
 * 1600 dariam ~32 px por linha. Não é assim que se fotografa um cupom longo. O
 * do Muffato de 41 itens (decisão 284) veio numa foto 3:4 comum, com o papel
 * numa faixa estreita no meio: a 1200 × 1600, sobraram poucos pixels por linha,
 * e o modelo passou a **inventar descrição** ("SABAO LIQ OMO 5L" virou "SABAO
 * LIQ COCO 500ML") e a pôr preço no item vizinho.
 *
 * O cabeçalho já dizia o que fazer: *"se a extração decepcionar em cupom
 * térmico de verdade, este número sobe antes de qualquer outra coisa"*. Subiu
 * para **todas** as fotos, e não só as de cupom longo, porque não há como
 * saber pelo formato: todo retrato de celular é 3:4, com cupom curto ou longo
 * dentro. Um 12 MP do iPhone vira ~2,4 × 3,2 mil px, ainda longe do teto de
 * 10 MB de `storage.rules`.
 *
 * ## Redução em ETAPAS, e não num `drawImage` só
 *
 * Encolher 12 MP para 1600 px de uma vez faz o navegador amostrar de longe
 * demais, e o primeiro detalhe a morrer é justamente traço fino — que é do que
 * cupom térmico é feito. Reduzir pela metade, repetidamente, faz cada passo
 * misturar pixels vizinhos, e o texto chega inteiro do outro lado.
 *
 * ## Sempre JPEG na saída, e isso não é preguiça
 *
 * WebP sairia ~30% menor. Não vale **hoje**: a imagem é entrada de um modelo que
 * é trocável por uma string, e nem todo modelo atrás do OpenRouter aceita WebP —
 * seria acoplar "como arquivamos" a "o que o modelo do mês come". O ganho grande
 * já veio da resolução (um 4 MB vira algo em torno de 400 KB); os 30% restantes
 * ficam nomeados para quando o formato de entrada estiver assentado.
 *
 * **E converter para JPEG conserta um problema de verdade:** iPhone entrega
 * **HEIC**, que o modelo pode recusar. Passar pelo canvas normaliza tudo para um
 * formato que qualquer coisa lê.
 *
 * ## Falhar aqui NUNCA impede o upload
 *
 * Toda saída de erro devolve o arquivo original. Um cupom que não sobe porque o
 * otimizador tropeçou é infinitamente pior do que um cupom que sobe grande — e
 * `storage.rules` já tem o teto de 10 MB para o caso extremo.
 */

/** O maior lado, em pixels. Ver o cabeçalho. */
export const LADO_MAXIMO = 3200

/** Qualidade do JPEG. Abaixo de ~0,75 o JPEG cria halo em volta de traço fino. */
export const QUALIDADE = 0.82

/** Se a decodificação travar, o upload segue com o original. */
const TIMEOUT_MS = 15_000

/**
 * Os tipos que já são seguros de guardar e de mandar ao modelo. Fora desta
 * lista — **HEIC do iPhone**, principalmente —, o resultado do canvas vale mesmo
 * que ele fique maior: legível por todo mundo vale mais do que alguns
 * quilobytes.
 */
const TIPOS_SEGUROS = ['image/jpeg', 'image/png', 'image/webp']

export type ImagemPreparada = {
  blob: Blob
  /** Para a tela poder dizer o que economizou. */
  bytesOriginais: number
  bytesFinais: number
  /** `false` quando deu ruim e o original seguiu — nunca é erro fatal. */
  processada: boolean
}

export async function prepareImage(file: Blob): Promise<ImagemPreparada> {
  const original: ImagemPreparada = {
    blob: file,
    bytesOriginais: file.size,
    bytesFinais: file.size,
    processada: false,
  }

  try {
    // O canvas é conferido **antes** de decodificar a imagem: sem contexto 2D
    // não há o que fazer, e descobrir isso depois seria trabalho jogado fora.
    // É também o caminho que roda no jsdom, onde canvas não existe.
    const canvas = document.createElement('canvas')
    if (typeof canvas.getContext !== 'function') return original
    const ctx = canvas.getContext('2d')
    if (ctx === null) return original

    const img = await carregar(file)
    // `naturalWidth`/`naturalHeight` já vêm com a **orientação do EXIF
    // aplicada**: desde Chrome 81 e Safari 13.1 o padrão é
    // `image-orientation: from-image`. Foto de iPhone em retrato chega em pé.
    // (`createImageBitmap` seria o contrário — lá a orientação é ignorada por
    // padrão, e o cupom sairia deitado.)
    const alvo = fitWithin(img.naturalWidth, img.naturalHeight, LADO_MAXIMO)
    if (alvo.width <= 0 || alvo.height <= 0) return original

    const fonte = reduzirEmEtapas(img, alvo)

    canvas.width = alvo.width
    canvas.height = alvo.height
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(fonte, 0, 0, alvo.width, alvo.height)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', QUALIDADE)
    })
    if (blob === null || blob.size === 0) return original

    // Ficou maior? Fica o original — **a não ser** que o original seja de um
    // tipo que o modelo pode recusar. Ver `TIPOS_SEGUROS`.
    const seguro = TIPOS_SEGUROS.includes(file.type)
    if (seguro && blob.size >= file.size) return original

    return {
      blob,
      bytesOriginais: file.size,
      bytesFinais: blob.size,
      processada: true,
    }
  } catch (erro: unknown) {
    // Fica no console porque é diagnóstico, e não vira erro na tela: o upload
    // segue com o original.
    console.error('prepareImage: não deu para processar, subindo o original', erro)
    return original
  }
}

/**
 * Reduz pela metade até faltar menos de 2× para o destino.
 *
 * Devolve a própria imagem quando não há o que fazer — assim quem chama sempre
 * tem uma fonte válida para o `drawImage` final.
 */
function reduzirEmEtapas(
  img: HTMLImageElement,
  alvo: { width: number; height: number },
): CanvasImageSource {
  let largura = img.naturalWidth
  let altura = img.naturalHeight
  let fonte: CanvasImageSource = img

  while (largura > alvo.width * 2 && altura > alvo.height * 2) {
    const passo = document.createElement('canvas')
    passo.width = Math.max(1, Math.floor(largura / 2))
    passo.height = Math.max(1, Math.floor(altura / 2))
    const ctx = passo.getContext('2d')
    if (ctx === null) return fonte
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(fonte, 0, 0, passo.width, passo.height)
    fonte = passo
    largura = passo.width
    altura = passo.height
  }

  return fonte
}

/** Decodifica o arquivo, com teto de tempo. */
function carregar(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    const limpar = () => {
      clearTimeout(relogio)
      URL.revokeObjectURL(url)
    }
    const relogio = setTimeout(() => {
      limpar()
      reject(new Error('prepareImage: a imagem não decodificou a tempo'))
    }, TIMEOUT_MS)

    img.onload = () => {
      limpar()
      resolve(img)
    }
    img.onerror = () => {
      limpar()
      // Acontece de verdade: HEIC num navegador que não decodifica HEIC.
      reject(new Error('prepareImage: o navegador não decodificou esta imagem'))
    }
    img.src = url
  })
}
